import React, { useRef, useState, useEffect } from "react";
import AppAppBar from "./views/AppAppBar";
import AboutHero from "./views/AboutHero";
import AppFooter from "./views/AppFooter";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "./components/Typography";
import withRoot from "../../withRoot";
import worldMap from "../../assets/world-map.png";
import rootsImage from "../../assets/roots.png";
import en from "../../data/en.json";

const About: React.FC = () => {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleSections, setVisibleSections] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.5 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AppAppBar />
      <AboutHero />

      <Box
        sx={{
          maxWidth: { xs: "100vw", md: "50vw" }, // Mobile: 100vw, Desktop: 50vw
          textAlign: "center",
          m: "0 auto",
          padding: { xs: "10px 20px", md: "20px" }, // Horizontal padding on mobile to prevent overflow
          mt: { xs: 4, md: 8 }, // Consistent margin with other pages
          mb: { xs: 4, md: 8 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", fontSize: { xs: "1.2rem", md: "1.5rem" } }}
        >
          {en.marketing.aboutContent.title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            fontSize: { xs: "1rem", md: "1.2rem" },
            lineHeight: "2",
          }}
        >
          {en.marketing.aboutContent.description}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 2,
            fontSize: { xs: "1rem", md: "1.2rem" },
            lineHeight: "2",
          }}
        >
          {en.marketing.aboutContent.descriptionTwo}
        </Typography>
      </Box>

      {/* Global Presence Section */}
      <Grid
        container
        spacing={0}
        sx={{ mt: { xs: 4, md: 8 }, mb: 0 }}
        alignItems="stretch"
      >
        <Grid
          item
          xs={12}
          md={6}
          sx={{ order: { xs: 2, md: 1 }, display: "flex" }}
        >
          <Box
            component="img"
            src={worldMap}
            alt="World Map"
            sx={{
              width: "100%",
              height: { xs: "300px", md: "400px" }, // Fixed consistent height
              objectFit: "cover",
              display: "block", // Ensure no extra space below image
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            bgcolor: "black",
            color: "white",
            p: { xs: "4vw", md: "4vw" }, // Match Main.tsx padding style
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            order: { xs: 1, md: 2 }, // Text first on mobile
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              mb: 2,
              color: "white",
              fontSize: { xs: "1rem", md: "1.25rem" },
            }}
          >
            GLOBAL PRESENCE
          </Typography>
          <Typography
            variant="h5"
            sx={{ mb: 2, fontSize: { xs: "1.25rem", md: "1.5rem" } }}
          >
            InkBowl serves a diverse global community of students spanning 33
            countries.
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
          >
            We aim to continue expanding our reach and welcome students from
            every corner of the world to join our network and take advantage of
            our creative, academic, and professional resources.
          </Typography>
        </Grid>
      </Grid>

      {/* Our Roots Section - connects directly to footer */}
      <Grid
        container
        spacing={0}
        alignItems="stretch"
        sx={{ mb: 0, flexGrow: 1 }}
      >
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            bgcolor: "black",
            color: "white",
            p: { xs: "4vw", md: "4vw" }, // Match Main.tsx padding style
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              mb: 2,
              color: "white",
              fontSize: { xs: "1rem", md: "1.25rem" },
            }}
          >
            OUR ROOTS
          </Typography>
          <Typography
            variant="h5"
            sx={{ mb: 2, fontSize: { xs: "1.25rem", md: "1.5rem" } }}
          >
            InkBowl originated as a dedicated platform for the publication of
            student literature and art.
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}
          >
            Since its inception, InkBowl has experienced substantial evolution
            and managerial transformations. We have broadened our scope to
            address a wider range of student interests and necessities,
            encompassing both academic and extracurricular domains. Loyal to our
            roots, publication remains one of our key features.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <Box
            component="img"
            src={rootsImage}
            alt="Roots"
            sx={{
              width: "100%",
              height: { xs: "300px", md: "400px" }, // Fixed consistent height matching the first image
              objectFit: "cover",
              display: "block", // Ensure no extra space below image
            }}
          />
        </Grid>
      </Grid>

      <AppFooter />
    </Box>
  );
};

export default withRoot(About);
